import { useCallback, useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import {
  AnchorCommentTextarea,
  hasIncompleteMention,
  validateAnchorCommentText
} from '@jarvis/platform-file-review';
import { FloatingCommentDialog } from '../fileReview/FloatingCommentDialog';
import '../pdfViewer/JarvisPdfViewer.css';

export interface DocumentCommentPopoverProps {
  mountId?: string;
  /** File path within the mount; scopes WebOps parent mounts to the covering client folder. */
  path?: string;
  onCancel: () => void;
  onSave: (comment: string) => void | Promise<void>;
  onValidationError?: (message: string) => void;
}

export function DocumentCommentPopover({
  mountId,
  path,
  onCancel,
  onSave,
  onValidationError
}: DocumentCommentPopoverProps) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      document
        .querySelector<HTMLTextAreaElement>('.floating-comment-dialog textarea')
        ?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  const handleSave = useCallback(async () => {
    const text = draft.trim();
    if (!text || saving) return;
    const validationError = validateAnchorCommentText(text);
    if (validationError) {
      onValidationError?.(validationError);
      return;
    }
    setSaving(true);
    try {
      await onSave(text);
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, onValidationError, saving]);

  const saveDisabled = saving || !draft.trim() || hasIncompleteMention(draft);

  return (
    <FloatingCommentDialog ariaLabel="Comment on selection" dialogClassName="jarvis-pdf-selection-comment">
      <AnchorCommentTextarea
        mountId={mountId}
        path={path}
        className="jarvis-pdf-highlight-popover__input"
        rows={9}
        placeholder="Comment on selection… Type @ to pick someone to notify"
        value={draft}
        disabled={saving}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setDraft(event.target.value)}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            void handleSave();
          }
        }}
      />
      <div className="jarvis-pdf-highlight-popover__actions">
        <button
          type="button"
          className="jarvis-pdf-highlight-popover__cancel"
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="jarvis-pdf-highlight-popover__save"
          disabled={saveDisabled}
          onClick={() => void handleSave()}
        >
          Save
        </button>
      </div>
    </FloatingCommentDialog>
  );
}
